/**
 * Shared Date Range Filter Service
 * 
 * Unified date-range filtering system for Project Map and Revenue Projections.
 * Backend-driven, reusable across dashboards.
 * 
 * Source of truth: scheduled_completion_date (or scheduled_date as fallback)
 */

import { 
  addDays, 
  startOfWeek, 
  endOfWeek, 
  addWeeks,
  startOfMonth,
  endOfMonth, 
  endOfQuarter, 
  endOfYear,
  isWithinInterval,
  isBefore,
  isAfter,
  format,
  startOfDay,
  endOfDay
} from 'date-fns';

// Preset filter options
export const DATE_FILTER_PRESETS = {
  NEXT_7_DAYS: 'next_7_days',
  NEXT_14_DAYS: 'next_14_days',
  END_OF_MONTH: 'end_of_month',
  END_OF_QUARTER: 'end_of_quarter',
  END_OF_YEAR: 'end_of_year',
  INSTALL_WEEK_CURRENT: 'install_week_current',
  INSTALL_WEEK_NEXT: 'install_week_next',
  INSTALL_WEEK_PLUS_2: 'install_week_plus_2',
  INSTALL_WEEK_PLUS_3: 'install_week_plus_3',
  CUSTOM_RANGE: 'custom_range',
  ALL: 'all',
};

// Preset labels for UI
export const DATE_FILTER_LABELS = {
  [DATE_FILTER_PRESETS.NEXT_7_DAYS]: 'Next 7 Days',
  [DATE_FILTER_PRESETS.NEXT_14_DAYS]: 'Next 14 Days',
  [DATE_FILTER_PRESETS.END_OF_MONTH]: 'End of Month',
  [DATE_FILTER_PRESETS.END_OF_QUARTER]: 'End of Quarter',
  [DATE_FILTER_PRESETS.END_OF_YEAR]: 'End of Year',
  [DATE_FILTER_PRESETS.INSTALL_WEEK_CURRENT]: 'This Install Week',
  [DATE_FILTER_PRESETS.INSTALL_WEEK_NEXT]: 'Next Install Week',
  [DATE_FILTER_PRESETS.INSTALL_WEEK_PLUS_2]: 'Install Week +2',
  [DATE_FILTER_PRESETS.INSTALL_WEEK_PLUS_3]: 'Install Week +3',
  [DATE_FILTER_PRESETS.CUSTOM_RANGE]: 'Custom Range',
  [DATE_FILTER_PRESETS.ALL]: 'All Jobs',
};

/**
 * Calculate date range boundaries for a given preset
 * @param {string} preset - One of DATE_FILTER_PRESETS
 * @param {object} customRange - { from: Date, to: Date } for custom range
 * @returns {{ start: Date, end: Date, label: string }}
 */
export const calculateDateRange = (preset, customRange = null) => {
  const now = new Date();
  const today = startOfDay(now);
  
  switch (preset) {
    case DATE_FILTER_PRESETS.NEXT_7_DAYS:
      return {
        start: today,
        end: endOfDay(addDays(today, 7)),
        label: `${format(today, 'MMM d')} - ${format(addDays(today, 7), 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.NEXT_14_DAYS:
      return {
        start: today,
        end: endOfDay(addDays(today, 14)),
        label: `${format(today, 'MMM d')} - ${format(addDays(today, 14), 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.END_OF_MONTH:
      return {
        start: today,
        end: endOfMonth(now),
        label: `Through ${format(endOfMonth(now), 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.END_OF_QUARTER:
      return {
        start: today,
        end: endOfQuarter(now),
        label: `Through Q${Math.ceil((now.getMonth() + 1) / 3)} ${format(now, 'yyyy')}`,
      };
      
    case DATE_FILTER_PRESETS.END_OF_YEAR:
      return {
        start: today,
        end: endOfYear(now),
        label: `Through ${format(endOfYear(now), 'MMM d, yyyy')}`,
      };
      
    case DATE_FILTER_PRESETS.INSTALL_WEEK_CURRENT:
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }), // Monday start
        end: endOfWeek(now, { weekStartsOn: 1 }),
        label: `Week of ${format(startOfWeek(now, { weekStartsOn: 1 }), 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.INSTALL_WEEK_NEXT:
      const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      return {
        start: nextWeekStart,
        end: endOfWeek(nextWeekStart, { weekStartsOn: 1 }),
        label: `Week of ${format(nextWeekStart, 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.INSTALL_WEEK_PLUS_2:
      const week2Start = startOfWeek(addWeeks(now, 2), { weekStartsOn: 1 });
      return {
        start: week2Start,
        end: endOfWeek(week2Start, { weekStartsOn: 1 }),
        label: `Week of ${format(week2Start, 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.INSTALL_WEEK_PLUS_3:
      const week3Start = startOfWeek(addWeeks(now, 3), { weekStartsOn: 1 });
      return {
        start: week3Start,
        end: endOfWeek(week3Start, { weekStartsOn: 1 }),
        label: `Week of ${format(week3Start, 'MMM d')}`,
      };
      
    case DATE_FILTER_PRESETS.CUSTOM_RANGE:
      if (customRange?.from && customRange?.to) {
        return {
          start: startOfDay(customRange.from),
          end: endOfDay(customRange.to),
          label: `${format(customRange.from, 'MMM d')} - ${format(customRange.to, 'MMM d')}`,
        };
      }
      // Fallback to all if no custom range provided
      return {
        start: null,
        end: null,
        label: 'Select dates',
      };
      
    case DATE_FILTER_PRESETS.ALL:
    default:
      return {
        start: null,
        end: null,
        label: 'All Time',
      };
  }
};

/**
 * Filter jobs by date range using scheduled_completion_date or scheduled_date
 * @param {Array} jobs - Array of job objects
 * @param {string} preset - One of DATE_FILTER_PRESETS
 * @param {object} customRange - { from: Date, to: Date } for custom range
 * @returns {Array} Filtered jobs
 */
export const filterJobsByDateRange = (jobs, preset, customRange = null) => {
  if (preset === DATE_FILTER_PRESETS.ALL) {
    return jobs;
  }
  
  const { start, end } = calculateDateRange(preset, customRange);
  
  if (!start || !end) {
    return jobs;
  }
  
  return jobs.filter(job => {
    // Use scheduled_completion_date first, fallback to scheduled_date
    const jobDateStr = job.scheduled_completion_date || job.scheduled_date;
    
    if (!jobDateStr) {
      // Jobs without dates are excluded from date-filtered views
      return false;
    }
    
    const jobDate = new Date(jobDateStr);
    
    return isWithinInterval(jobDate, { start, end });
  });
};

/**
 * Calculate revenue projections for a date range
 * @param {Array} jobs - Array of job objects
 * @param {string} preset - One of DATE_FILTER_PRESETS
 * @param {object} customRange - { from: Date, to: Date } for custom range
 * @returns {{ amount: number, jobs: Array, jobCount: number }}
 */
export const calculateRevenueProjection = (jobs, preset, customRange = null) => {
  const filteredJobs = filterJobsByDateRange(jobs, preset, customRange);
  
  const totalAmount = filteredJobs.reduce((sum, job) => {
    // Use remaining_balance first, fallback to contract_amount or estimated_value
    const revenue = job.remaining_balance || job.contract_amount || job.estimated_value || 0;
    return sum + revenue;
  }, 0);
  
  return {
    amount: totalAmount,
    jobs: filteredJobs.sort((a, b) => {
      const dateA = new Date(a.scheduled_completion_date || a.scheduled_date || '9999-12-31');
      const dateB = new Date(b.scheduled_completion_date || b.scheduled_date || '9999-12-31');
      return dateA - dateB;
    }),
    jobCount: filteredJobs.length,
  };
};

/**
 * Get list of upcoming install weeks for week picker
 * @param {number} weeksAhead - Number of weeks to generate
 * @returns {Array<{ value: string, label: string, start: Date, end: Date }>}
 */
export const getUpcomingInstallWeeks = (weeksAhead = 8) => {
  const now = new Date();
  const weeks = [];
  
  for (let i = 0; i < weeksAhead; i++) {
    const weekStart = startOfWeek(addWeeks(now, i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    
    weeks.push({
      value: `install_week_${i}`,
      label: i === 0 
        ? `This Week (${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')})` 
        : `Week of ${format(weekStart, 'MMM d')}`,
      start: weekStart,
      end: weekEnd,
      weekOffset: i,
    });
  }
  
  return weeks;
};

/**
 * Format a job's scheduled date for display
 * @param {object} job - Job object
 * @returns {string} Formatted date string
 */
export const formatJobScheduledDate = (job) => {
  const dateStr = job.scheduled_completion_date || job.scheduled_date;
  if (!dateStr) return 'Not scheduled';
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays <= 7) return `${diffDays}d away`;
  
  return format(date, 'MMM d');
};

export default {
  DATE_FILTER_PRESETS,
  DATE_FILTER_LABELS,
  calculateDateRange,
  filterJobsByDateRange,
  calculateRevenueProjection,
  getUpcomingInstallWeeks,
  formatJobScheduledDate,
};
